export interface Fees {
  tradingFee: number;
  maximumFeePercentage: number;
  minimumFee: number;
  currencyExchangeFee: number;
}

export interface Investment {
  initialInvestment: number;
  investmentPeriod: number;
  annualReturn: number;
}

export interface DataPoint {
  y: number;
  marker: { enabled: boolean };
}

export interface ReturnValues {
  value: number;
  cost: number;
  tradingFee: number;
  currencyExchangeFee: number;
  profitData: DataPoint[];
  costsData: DataPoint[];
}

const getStockValueAndCosts = (fees: Fees, investment: Investment): ReturnValues => {
  // Start values
  let value = investment.initialInvestment;
  let totalCurrencyExchangeFee = 0;

  // Arrays to store monthly data points
  const profitDataTemp: DataPoint[] = [];
  const costsDataTemp: DataPoint[] = [];

  // Calculate trading fee & check minimum and maximum for the initial purchase
  let tradingFee = (value * fees.tradingFee) / 100;

  // Ensure tradingFee is not below the minimum fee
  if (tradingFee < fees.minimumFee) {
    tradingFee = fees.minimumFee;
  }

  // Ensure tradingFee is not above the maximum allowed
  const maximumTradingFeeInEuros = (value * fees.maximumFeePercentage) / 100;
  if (tradingFee > maximumTradingFeeInEuros) {
    tradingFee = maximumTradingFeeInEuros;
  }

  // Calculate and apply currency exchange fee only on the initial investment
  const currencyExchangeFee = (value * fees.currencyExchangeFee) / 100;
  totalCurrencyExchangeFee += currencyExchangeFee;
  value -= tradingFee + currencyExchangeFee;

  // Calculate total number of months
  const months = investment.investmentPeriod * 12;

  for (let month = 0; month < months; month += 1) {
    // Monthly interest
    value += value * (investment.annualReturn / 12 / 100);

    // Determine if it's the first or final month
    const isFirstMonth = month === 0;
    const isFinalMonth = month === months - 1;

    // Calculate final trading fee only in the last month
    let currentTradingFee = tradingFee;
    if (isFinalMonth) {
      currentTradingFee = (value * fees.tradingFee) / 100;

      // Ensure final trading fee is not below the minimum fee
      if (currentTradingFee < fees.minimumFee) {
        currentTradingFee = fees.minimumFee;
      }

      // Ensure final trading fee is not above the maximum allowed
      const finalMaximumTradingFeeInEuros = (value * fees.maximumFeePercentage) / 100;
      if (currentTradingFee > finalMaximumTradingFeeInEuros) {
        currentTradingFee = finalMaximumTradingFeeInEuros;
      }

      // Deduct the final trading fee
      value -= currentTradingFee;
    }

    // Store the monthly value and cost with marker on the first and final month
    profitDataTemp.push({
      y: value,
      marker: { enabled: isFirstMonth || isFinalMonth },
    });

    costsDataTemp.push({
      y: tradingFee + totalCurrencyExchangeFee + (isFinalMonth ? currentTradingFee : 0),
      marker: { enabled: isFirstMonth || isFinalMonth },
    });
  }

  return {
    value,
    cost: tradingFee + totalCurrencyExchangeFee + tradingFee,
    tradingFee,
    currencyExchangeFee: totalCurrencyExchangeFee,
    profitData: profitDataTemp,
    costsData: costsDataTemp,
  };
};

export default getStockValueAndCosts;
